import { useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addToCart } from '@homeplay/app';
import { LOTTO_CONSTANTS } from '../../../constants';
import { useStakeInput } from '../../../hooks/useStakeInput';
import { useBallCounts } from '../../../hooks/useBallCounts';
import { useAssistantActions } from '../../../hooks/useLottoAssistantContext';
import { calculatePotentialPayout } from '../../../utils/common';
import { buildCartItemFromTicket } from '../../../utils/cartItemBuilder';
import { validateStake, validateBalls } from '../utils/validationPipeline';
import { createInitialState, ticketReducer } from '../utils/ticketReducer';

export const useTicketEditor = ({ ticket, onTicketUpdate, trackLottoEvent }) => {
  const reduxDispatch = useDispatch();
  const { setFlyingTicketSource } = useAssistantActions();

  const minStake = LOTTO_CONSTANTS.MIN_STAKE;
  const maxStake = LOTTO_CONSTANTS.MAX_STAKE;
  const initialStake = Math.max(
    minStake,
    Math.min(ticket.stake, LOTTO_CONSTANTS.MAX_STAKE)
  );

  const [state, dispatch] = useReducer(
    ticketReducer,
    createInitialState(ticket, initialStake)
  );

  const onTicketUpdateRef = useRef(onTicketUpdate);
  const baseTicketRef = useRef(ticket);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    onTicketUpdateRef.current = onTicketUpdate;
  }, [onTicketUpdate]);

  useEffect(() => {
    baseTicketRef.current = ticket;
  }, [
    ticket.ticket_id,
    ticket.draw_id,
    ticket.lottery_name,
    ticket.market_type,
    ticket.outcome?.id,
  ]);

  const stakeInputHook = useStakeInput({
    initialStake,
    minStake,
    maxStake,
  });

  useEffect(() => {
    dispatch({ type: 'SET_STAKE', payload: stakeInputHook.stake });
  }, [stakeInputHook.stake]);

  useEffect(() => {
    dispatch({ type: 'SET_STAKE_INPUT', payload: stakeInputHook.stakeInput });
  }, [stakeInputHook.stakeInput]);

  useEffect(() => {
    dispatch({ type: 'SET_IS_EDITING_STAKE', payload: stakeInputHook.isEditing });
  }, [stakeInputHook.isEditing]);

  const hasBalls = ticket.balls != null && ticket.balls.length > 0;
  const ballCountsData = useBallCounts({
    hasBalls,
    marketType: ticket.market_type,
    balls: ticket.balls,
    mainGridTotal: ticket.main_grid_total,
    editedNumbers: state.balls,
  });

  const stakeConstraints = useMemo(() => ({ minStake, maxStake }), [minStake, maxStake]);

  const ballConstraints = useMemo(
    () => ({
      minNormalCount: ballCountsData.minNormalCount,
      maxNormalCount: ballCountsData.maxNormalCount,
      requiredBonusCount: ballCountsData.requiredBonusCount,
      mainGridTotal: ticket.main_grid_total,
      secondaryGridTotal: ticket.secondary_grid_total,
      secondaryUnique: ticket.secondary_unique,
    }),
    [
      ballCountsData.minNormalCount,
      ballCountsData.maxNormalCount,
      ballCountsData.requiredBonusCount,
      ticket.main_grid_total,
      ticket.secondary_grid_total,
      ticket.secondary_unique,
    ]
  );

  const isStakeValid = useMemo(() => {
    const result = validateStake(state.stake, stakeConstraints);
    return result.isValid && stakeInputHook.isValid;
  }, [state.stake, stakeConstraints, stakeInputHook.isValid]);

  const isTicketValid = useMemo(() => {
    if (!hasBalls) return isStakeValid;
    const hasNaNs = state.balls.some((n) => Number.isNaN(n.value));
    return !hasNaNs && !state.validationError && isStakeValid;
  }, [hasBalls, state.balls, state.validationError, isStakeValid]);

  const validateBeforeSubmit = useCallback(() => {
    if (!hasBalls) return isStakeValid;
    const result = validateBalls(state.balls, ballConstraints);
    if (!result.isValid && result.errors.length > 0) {
      dispatch({ type: 'SET_VALIDATION_ERROR', payload: result.errors[0] });
      return false;
    }
    dispatch({ type: 'SET_VALIDATION_ERROR', payload: null });
    return isStakeValid;
  }, [hasBalls, state.balls, ballConstraints, isStakeValid]);

  const potentialPayout = useMemo(
    () =>
      calculatePotentialPayout(
        state.stake,
        ticket.outcome?.odds,
        ballCountsData.singleBetsCount,
        state.repeatForDraws
      ),
    [state.stake, ticket.outcome?.odds, ballCountsData.singleBetsCount, state.repeatForDraws]
  );

  const totalCost = useMemo(
    () => state.stake * ballCountsData.singleBetsCount * state.repeatForDraws,
    [state.stake, ballCountsData.singleBetsCount, state.repeatForDraws]
  );

  const notifyParentOfUpdate = useCallback(() => {
    if (!onTicketUpdateRef.current) return;
    const baseTicket = baseTicketRef.current;

    if (isTicketValid) {
      onTicketUpdateRef.current({
        ...baseTicket,
        stake: state.stake,
        balls: hasBalls ? state.balls : null,
        repeat_for_draws: state.repeatForDraws,
        change_odds_agreement: state.acceptOddsChanges,
        isModified: true,
        initialEditMode: false,
      });
    } else {
      onTicketUpdateRef.current(null);
    }
  }, [
    isTicketValid,
    hasBalls,
    state.balls,
    state.stake,
    state.repeatForDraws,
    state.acceptOddsChanges,
  ]);

  useEffect(() => {
    notifyParentOfUpdate();
  }, [notifyParentOfUpdate]);

  const actions = useMemo(
    () => ({
      setStake: (stake) => dispatch({ type: 'SET_STAKE', payload: stake }),
      setBalls: (balls) => dispatch({ type: 'SET_BALLS', payload: balls }),
      setRepeatForDraws: (draws) => dispatch({ type: 'SET_REPEAT_FOR_DRAWS', payload: draws }),
      setAcceptOddsChanges: (accept) =>
        dispatch({ type: 'SET_ACCEPT_ODDS_CHANGES', payload: accept }),
      setValidationError: (error) =>
        dispatch({ type: 'SET_VALIDATION_ERROR', payload: error }),
      handleStakeChange: stakeInputHook.handleChange,
      toggleStakeEdit: stakeInputHook.toggleEditing,
      stopStakeEdit: stakeInputHook.stopEditing,
      clearErrors: () => dispatch({ type: 'CLEAR_ERRORS' }),
    }),
    [stakeInputHook.handleChange, stakeInputHook.toggleEditing, stakeInputHook.stopEditing]
  );

  const handleAddToCart = useCallback(
    (ticketRef) => {
      if (!validateBeforeSubmit() || !isStakeValid) return;

      try {
        dispatch({ type: 'SET_ADD_TO_CART_ERROR', payload: null });

        const cartItem = buildCartItemFromTicket(
          ticket,
          hasBalls ? state.balls : null,
          state.stake,
          potentialPayout,
          state.repeatForDraws,
          state.acceptOddsChanges,
          ballCountsData.singleBetsCount
        );

        reduxDispatch(addToCart(cartItem));

        trackLottoEvent?.('ticket_added_to_cart', {
          ticket_id: ticket.ticket_id,
          draw_id: ticket.draw_id,
          draw_number: ticket.draw_number,
          draw_date: ticket.draw_date,
          lottery_name: ticket.lottery_name,
          market_type: ticket.market_type,
          market_title: ticket.market_title,
          outcome: ticket.outcome,
          balls: hasBalls ? state.balls : null,
          stake: state.stake * LOTTO_CONSTANTS.CENTS_MULTIPLIER,
          potential_payout: potentialPayout,
          repeat_for_draws: state.repeatForDraws,
        });

        if (ticketRef?.current) {
          setFlyingTicketSource?.(ticketRef.current);
        }
      } catch (error) {
        dispatch({
          type: 'SET_ADD_TO_CART_ERROR',
          payload: 'Failed to add to cart. Please try again.',
        });
      }
    },
    [
      validateBeforeSubmit,
      isStakeValid,
      ticket,
      hasBalls,
      state.balls,
      state.stake,
      potentialPayout,
      state.repeatForDraws,
      state.acceptOddsChanges,
      ballCountsData.singleBetsCount,
      reduxDispatch,
      setFlyingTicketSource,
      trackLottoEvent,
    ]
  );

  return {
    state: {
      stake: state.stake,
      stakeInput: state.stakeInput,
      balls: state.balls,
      repeatForDraws: state.repeatForDraws,
      acceptOddsChanges: state.acceptOddsChanges,
      isEditingStake: state.isEditingStake,
    },
    validation: {
      validationError: state.validationError,
      addToCartError: state.addToCartError,
      isStakeValid,
      isTicketValid,
      validateBeforeSubmit,
    },
    actions,
    derived: {
      potentialPayout,
      totalCost,
      singleBetsCount: ballCountsData.singleBetsCount,
      minNormalCount: ballCountsData.minNormalCount,
      maxNormalCount: ballCountsData.maxNormalCount,
      requiredBonusCount: ballCountsData.requiredBonusCount,
    },
    handlers: { handleAddToCart },
  };
};
